/**
 * Walking round corners.
 *
 * `CMD_MOVE_TO` is a straight line: the server walks the player at its target and
 * `confine` stops them at the first wall. So clicking past a corner did nothing but
 * press the character into rock — the single most-felt limitation in the game, and
 * the reason four separate features went unverified.
 *
 * **Pathfinding lives on the client, and the server does not change.** That looks
 * backwards for a project this strict about authority, and it is the opposite:
 *
 *  - Routing is not an outcome. The server still decides where the player *is*, and
 *    still refuses to move them through a wall. All it receives is a sequence of
 *    straight-line legs it would have accepted individually anyway, so a modified
 *    client gains nothing it did not already have.
 *  - The client predicts its own movement. If the server walked a path while the
 *    predictor walked a straight line, every frame would be a correction — the
 *    rubber-banding prediction exists to remove. Pathing here means the predictor
 *    predicts exactly the leg it just asked for, which is what it already did.
 *  - The client already holds the whole grid. The ZONE frame carries every tile, and
 *    `isWalkable` mirrors `TileKind.Walkable()` in Go, so both halves agree on what
 *    is solid without anything new on the wire.
 *
 * Mob pathfinding is a *server* concern and is deliberately not in this file. A mob
 * that walks into a wall is still stuck; that needs the same search inside
 * `internal/game/ai.go`.
 */

import type { ZonePayload } from '../net/protocol';
import { isWalkable, tileAt, tileCentre } from './zone/layout';

export interface Point {
  x: number;
  z: number;
}

interface Cell {
  col: number;
  row: number;
}

/**
 * The tile containing a world point. Mirrors `Layout.gridOf` in Go.
 *
 * Both must agree exactly, the same way `worldCentre` and `tileCentre` do: a client
 * that thinks it is standing in a different cell than the server will path out of a
 * wall it is not in.
 */
export function gridOf(zone: ZonePayload, at: Point): Cell {
  const halfW = (zone.width * zone.tileSize) / 2;
  const halfH = (zone.height * zone.tileSize) / 2;
  return {
    col: Math.floor((at.x + halfW) / zone.tileSize),
    row: Math.floor((at.z + halfH) / zone.tileSize),
  };
}

function walkableCell(zone: ZonePayload, col: number, row: number): boolean {
  return isWalkable(tileAt(zone, col, row).kind);
}

/** Eight directions, in a fixed order so a tie is always broken the same way. */
const STEPS: readonly Cell[] = [
  { col: 0, row: -1 },
  { col: 1, row: 0 },
  { col: 0, row: 1 },
  { col: -1, row: 0 },
  { col: 1, row: -1 },
  { col: 1, row: 1 },
  { col: -1, row: 1 },
  { col: -1, row: -1 },
];

/**
 * A route from one world point to another, as waypoints to walk in order.
 *
 * Empty when there is no route at all. The last waypoint is the destination itself
 * when it is reachable, or the reachable cell closest to it when it is not —
 * clicking into a wall should walk you as near as the geometry allows rather than
 * doing nothing, which is exactly the old behaviour's failing.
 */
export function findPath(zone: ZonePayload, from: Point, to: Point): Point[] {
  const start = gridOf(zone, from);
  const goal = gridOf(zone, to);

  if (!walkableCell(zone, start.col, start.row)) return [];

  // Already there: no legs to walk. The caller keeps its straight-line move for
  // within-tile adjustments, which is what makes short clicks feel immediate.
  if (start.col === goal.col && start.row === goal.row) return [to];

  const key = (c: number, r: number) => r * zone.width + c;
  const open: Cell[] = [start];
  const gScore = new Map<number, number>([[key(start.col, start.row), 0]]);
  const cameFrom = new Map<number, Cell>();
  const closed = new Set<number>();

  /** The reachable cell that got nearest the goal, for an unreachable target. */
  let best = start;
  let bestH = heuristic(start, goal);

  // A linear scan for the lowest score rather than a heap. The grid is 121 cells;
  // a heap would be more code, more ways to be wrong, and no faster at this size —
  // and the scan is trivially deterministic, which a heap's tie-breaking is not.
  while (open.length > 0) {
    let at = 0;
    for (let i = 1; i < open.length; i++) {
      const a = open[i]!;
      const b = open[at]!;
      const fa = (gScore.get(key(a.col, a.row)) ?? Infinity) + heuristic(a, goal);
      const fb = (gScore.get(key(b.col, b.row)) ?? Infinity) + heuristic(b, goal);
      if (fa < fb) at = i;
    }
    const current = open.splice(at, 1)[0]!;
    const currentKey = key(current.col, current.row);
    closed.add(currentKey);

    const h = heuristic(current, goal);
    if (h < bestH) {
      best = current;
      bestH = h;
    }
    if (current.col === goal.col && current.row === goal.row) {
      return simplify(zone, trace(cameFrom, current, zone, key), to, true);
    }

    for (const step of STEPS) {
      const col = current.col + step.col;
      const row = current.row + step.row;
      if (!walkableCell(zone, col, row)) continue;

      // A diagonal may not cut a corner. Without this the path slips between two
      // solid tiles that touch only at their corners — through the gap between two
      // houses, which the server will not walk and the player cannot see.
      if (step.col !== 0 && step.row !== 0) {
        if (!walkableCell(zone, current.col + step.col, current.row)) continue;
        if (!walkableCell(zone, current.col, current.row + step.row)) continue;
      }

      const neighbourKey = key(col, row);
      if (closed.has(neighbourKey)) continue;

      const cost = step.col !== 0 && step.row !== 0 ? Math.SQRT2 : 1;
      const tentative = (gScore.get(currentKey) ?? Infinity) + cost;
      if (tentative >= (gScore.get(neighbourKey) ?? Infinity)) continue;

      gScore.set(neighbourKey, tentative);
      cameFrom.set(neighbourKey, current);
      if (!open.some((c) => c.col === col && c.row === row)) open.push({ col, row });
    }
  }

  // Unreachable. Walk as close as the geometry allows.
  if (best.col === start.col && best.row === start.row) return [];
  return simplify(zone, trace(cameFrom, best, zone, key), to, false);
}

function heuristic(a: Cell, b: Cell): number {
  const dc = Math.abs(a.col - b.col);
  const dr = Math.abs(a.row - b.row);
  // Octile: the true cost of the cheapest unobstructed 8-way walk.
  return Math.max(dc, dr) + (Math.SQRT2 - 1) * Math.min(dc, dr);
}

/** Walks the parent chain back to the start, then reverses it. */
function trace(
  cameFrom: Map<number, Cell>,
  end: Cell,
  zone: ZonePayload,
  key: (c: number, r: number) => number,
): Cell[] {
  const cells = [end];
  let at = end;
  // Bounded by the grid: a cycle in the parent chain would otherwise hang the
  // render loop, and a hang is far worse than a wrong path.
  for (let i = 0; i < zone.width * zone.height; i++) {
    const parent = cameFrom.get(key(at.col, at.row));
    if (!parent) break;
    cells.push(parent);
    at = parent;
  }
  return cells.reverse();
}

/**
 * Drops waypoints that can be walked past in a straight line.
 *
 * Without this a path is one waypoint per tile: a blocky staircase, and one
 * `CMD_MOVE_TO` per eight world units. Pulling the string straight leaves the
 * corners and nothing else — usually two or three legs across a whole zone.
 */
function simplify(
  zone: ZonePayload,
  cells: Cell[],
  destination: Point,
  reachable: boolean,
): Point[] {
  const points = cells.map((c) => tileCentre(zone, c.col, c.row));
  // The exact point clicked, rather than the centre of its tile — otherwise every
  // move snaps to a grid the player cannot see.
  if (reachable) points[points.length - 1] = destination;

  const out: Point[] = [];
  let from = 0;
  while (from < points.length - 1) {
    // The furthest waypoint still reachable in a straight line from `from`.
    let to = from + 1;
    for (let candidate = points.length - 1; candidate > from + 1; candidate--) {
      if (clearLine(zone, points[from]!, points[candidate]!)) {
        to = candidate;
        break;
      }
    }
    out.push(points[to]!);
    from = to;
  }
  return out;
}

/**
 * Whether a straight walk between two points stays on walkable ground.
 *
 * Sampled rather than solved: a proper supercover traversal is more code for a test
 * that only needs to be conservative. The step is a third of a tile, which cannot
 * skip a tile at any angle.
 */
export function clearLine(zone: ZonePayload, from: Point, to: Point): boolean {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const distance = Math.hypot(dx, dz);
  if (distance === 0) return true;

  const step = zone.tileSize / 3;
  const samples = Math.ceil(distance / step);
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const cell = gridOf(zone, { x: from.x + dx * t, z: from.z + dz * t });
    if (!walkableCell(zone, cell.col, cell.row)) return false;
  }
  return true;
}

/** How close counts as having reached a waypoint, in world units. */
export const WAYPOINT_REACHED = 0.9;

/**
 * A route being walked: the remaining waypoints, and when to send the next.
 *
 * Holds no clock and sends nothing itself — the caller asks whether it is time for
 * the next leg and does the sending, so the whole thing is testable without a socket
 * or a frame loop.
 */
export class Route {
  private waypoints: Point[] = [];

  /** Waypoints still to walk, for tests and diagnostics. */
  get remaining(): number {
    return this.waypoints.length;
  }

  /** True while there is somewhere left to go. */
  get active(): boolean {
    return this.waypoints.length > 0;
  }

  /**
   * Starts a route and returns its first leg, or null when there is nothing to
   * walk. Replaces whatever was being walked — a new click cancels the old route.
   */
  start(waypoints: readonly Point[]): Point | null {
    this.waypoints = waypoints.slice();
    return this.waypoints[0] ?? null;
  }

  /** Abandons the route. An attack order, or arriving. */
  clear(): void {
    this.waypoints = [];
  }

  /**
   * Given where the player is, the next leg to send — or null to keep walking.
   *
   * Only reports a leg when one has just been reached, so the caller sends exactly
   * one `CMD_MOVE_TO` per corner rather than one per frame.
   */
  advance(at: Point, reached = WAYPOINT_REACHED): Point | null {
    const current = this.waypoints[0];
    if (!current) return null;
    if (Math.hypot(current.x - at.x, current.z - at.z) > reached) return null;

    this.waypoints.shift();
    return this.waypoints[0] ?? null;
  }
}
